import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faTrophy, faSync, faCrown, faHandFist, faXmark, faEnvelope, faGear, faHeartPulse, faBars, faArrowLeft, faRightFromBracket, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getSportIcon } from '../utils/sportEmoji';
import WeeklyCalendar from '../components/WeeklyCalendar';
import LadderView from '../components/LadderView';
import ChallengeCard from '../components/ChallengeCard';
import PlayerStatsTooltip from '../components/PlayerStatsTooltip';
import DuelCountdownButton from '../components/DuelCountdownButton';
import PointsSummary from '../components/PointsSummary';
import Footer from '../components/Footer';
import SportBreakdownBar from '../components/SportBreakdownBar';
import BadgeBar from '../components/BadgeBar';
import OpponentProfileCard from '../components/OpponentProfileCard';
import WeeklyBestCard from '../components/WeeklyBestCard';
import ItemBar from '../components/ItemBar';
import ItemRewardModal from '../components/ItemRewardModal';
import { sumPoints, sumMinutes } from '../utils/scoring';
import { Activity, Opponent, WeekData, DailyChallenge, PlayerStats, UserItem } from '../types/activity';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import '../styles/Welcome.css';
import '../styles/ItemBar.css';
import { getEloColor } from '../utils/ladder';

import logo from '../assets/logo.png';

interface UnseenResult {
  matchup_id: number;
  week_label: string;
  result: 'win' | 'loss' | 'draw';
  my_points: number;
  opp_points: number;
  opponent_name: string;
}

interface AdminReview {
  id: number;
  activity_id: number;
  decision: 'approved' | 'recalculated';
  old_total_points: number;
  new_total_points: number;
  activity_name: string;
  sport_type: string;
  created_at: string;
}

interface Props {
  onNavigateToDuel: () => void;
  onReplayMatchup: (matchupId: number) => void;
  onNavigateToArchive: () => void;
  onNavigateToDuelboard: () => void;
  onNavigateToLeaderboard: () => void;
  onNavigateToSettings: () => void;
  onNavigateToProfile: () => void;
  onNavigateToPlayerProfile: (playerId: number) => void;
}

export default function DashboardPage({ onNavigateToDuel, onReplayMatchup, onNavigateToArchive, onNavigateToDuelboard, onNavigateToLeaderboard, onNavigateToSettings, onNavigateToProfile, onNavigateToPlayerProfile }: Props) {
  const { token, logout } = useAuth();
  const isOnline = useOnlineStatus();
  const [week, setWeek] = useState<WeekData | null>(null);
  const [myBadges, setMyBadges] = useState<any[]>([]);
  const [oppBadges, setOppBadges] = useState<any[]>([]);
  const [unseenBadges, setUnseenBadges] = useState<any[]>([]);
  const [opponentActivities, setOpponentActivities] = useState<Activity[]>([]);
  const [opponentWorkoutCount, setOpponentWorkoutCount] = useState<number>(0);
  const [levelValue, setLevelValue] = useState<number>(1);
  const [eloRating, setEloRating] = useState<number>(1000);
  const [oppEloRating, setOppEloRating] = useState<number>(1000);
  const [topRating, setTopRating] = useState<number>(0);
  const [topPlayerAvatar, setTopPlayerAvatar] = useState<string | null>(null);
  const [myAvatar, setMyAvatar] = useState<string | null>(null);
  const [myName, setMyName] = useState<string>('You');
  const [myId, setMyId] = useState<number | null>(null);
  const [myStats, setMyStats] = useState<PlayerStats | null | false>(null);
  const [opponentStats, setOpponentStats] = useState<PlayerStats | null | false>(null);
  const [topTooltip, setTopTooltip] = useState<{ which: 'mine' | 'opp'; x: number; y: number } | null>(null);  useScrollDismiss(topTooltip, setTopTooltip);  const [challenge, setChallenge] = useState<DailyChallenge | null>(null);
  const [weekChallenges, setWeekChallenges] = useState<Record<string, DailyChallenge>>({});
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [resultViewed, setResultViewed] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [unseenResult, setUnseenResult] = useState<UnseenResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncCooldown, setSyncCooldown] = useState(0); // seconds remaining
  const [syncMessage, setSyncMessage] = useState<{text: string; type: 'success' | 'error' | 'warning'} | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [showSyncTooltip, setShowSyncTooltip] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [adminReviews, setAdminReviews] = useState<AdminReview[]>([]);
  const [showOpponentProfile, setShowOpponentProfile] = useState(false);
  const [showWeeklyBest, setShowWeeklyBest] = useState(false);
  const [myUntrusted, setMyUntrusted] = useState<{ untrusted: boolean; untrusted_at?: string; recalc_count?: number } | null>(null);
  const [uiStates, setUiStates] = useState<any>(null);
  const [currentDayIndex, setCurrentDayIndex] = useState(() => {
    // Heutigen Wochentag berechnen (Mo=0, Di=1, ..., So=6)
    const now = new Date();
    return (now.getDay() + 6) % 7; // JS: So=0, Mo=1 → Mo=0, So=6
  });
  const [barVisible, setBarVisible] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showItemReward, setShowItemReward] = useState(false);
  const closeSettingsPanel = useCallback(() => setShowSettingsPanel(false), []);


  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // SportBreakdownBar sichtbar machen wenn Woche geladen ist
  useEffect(() => {
    if (week) setTimeout(() => setBarVisible(true), 300);
  }, [week]);

  useEffect(() => {
    if (!token) return;
    api.get('/player/me', token).then((data) => {
      if (data?.level_value) setLevelValue(Number(data.level_value));
      if (data?.elo_rating)  setEloRating(Number(data.elo_rating));
      if (data?.avatar_url)  setMyAvatar(data.avatar_url);
      if (data?.display_name) setMyName(data.display_name);
      if (data?.id) setMyId(Number(data.id));
      // Welcome-Banner: DB-basiert (welcomed_at_utc)
      // Spieler mit mindestens einem gespielten Duel brauchen kein Welcome-Banner
      if (data?.welcomed_at_utc) {
        setShowWelcome(false);
      } else if (data?.elo_games && Number(data.elo_games) > 0) {
        // Erfahrene Spieler (älteres Feature): proaktiv als gesehen markieren
        api.post('/player/me/welcome', {}, token);
      }
    });
    // Fetch top onfire rating from leaderboard
    api.get('/duel/leaderboard', token).then((data) => {
      if (data?.ranking?.length) {

        const sorted = [...data.ranking].sort((a: {elo_rating: number}, b: {elo_rating: number}) => b.elo_rating - a.elo_rating);
        setTopRating(sorted[0].elo_rating);
        if (sorted[0].avatar_url) setTopPlayerAvatar(sorted[0].avatar_url);
      }
    });
    // Fetch player_ui_state (fuer Spin, Reveal, Trophy Daten)
    api.get('/player/me/ui-states', token).then((data: any) => {
      if (data?.states) setUiStates(data.states);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    api.get('/challenges/today', token).then((data) => {
      if (data?.challenge) setChallenge(data.challenge);
    });
    api.get('/challenges/week', token).then((data) => {
      if (data?.challenges) setWeekChallenges(data.challenges);
    });
  }, [token]);

  // Item Reward wird NICHT automatisch im Dashboard gezeigt –
  // nur vom 'Item Reward' Button im DuelScorePage oder beim Back-Button ausgelöst

  // ── Load badges ──
  useEffect(() => {
    if (!token) return;
    api.get('/player/me/badges', token)
      .then((data: any) => {
        if (data?.badges) setMyBadges(data.badges);
      })
      .catch(() => {});
    api.get('/player/me/badges/unseen', token)
      .then((data: any) => {
        if (data?.unseen?.length > 0) setUnseenBadges(data.unseen);
      })
      .catch(() => {});
  }, [token]);

  // ── Load opponent badges ──
  useEffect(() => {
    if (!token || !week?.opponent?.id || week?.opponent?.is_bot) return;
    api.get(`/player/${week.opponent.id}/badges`, token)
      .then((data: any) => {
        if (data?.badges) setOppBadges(data.badges);
      })
      .catch(() => {});
  }, [token, week?.opponent?.id, week?.opponent?.is_bot]);

  useEffect(() => {
    if (!token) return;
    api.get('/player/me/week', token).then((data) => {
      setWeek(data);
      setLoading(false);
      if (data?.last_synced_at) setLastSyncedAt(data.last_synced_at);
      // Welcome-Banner: prüfe DB (welcomed_at_utc kommt von /player/me useEffect)
      // Steckbrief: prüfe DB (steckbrief_seen_at_utc aus Backend)
      const currentMatchupId = data?.opponent?.matchup_id;
      if (currentMatchupId && data?.opponent?.id && !data?.opponent?.is_bot
          && typeof currentMatchupId === 'number' && currentMatchupId > 0) {
        if (!data?.opponent?.steckbrief_seen_at_utc) {
          setShowOpponentProfile(true);
        }
      }
      // Unseen-Result-Banner: prüfe DB (result_seen_at_utc aus Backend)
      // ⚠️ Erst ab Montag 03:00 UTC anzeigen (= 9h nach Deadline Sonntag 18:00 UTC)
      if (currentMatchupId) {
        const cycleDeadlineMs = data?.cycle?.workout_deadline_utc
          ? new Date(data.cycle.workout_deadline_utc.replace(' ', 'T') + 'Z').getTime()
          : 0;
        const resultShowAfterMs = cycleDeadlineMs + 9 * 60 * 60 * 1000; // +9h = Mo 03:00 UTC
        if (Date.now() > resultShowAfterMs && !data?.opponent?.result_seen_at_utc) {
          setUnseenResult({
            matchup_id:    currentMatchupId,
            week_label:    data?.cycle?.week_label ?? '',
            result:        'win',
            my_points:     0,
            opp_points:    0,
            opponent_name: data?.opponent?.display_name ?? 'Opponent',
          });
        }
      }
    });
  }, [token]);

  // Archiv-Check: Prüfe ob es ein nicht-gesehenes Ergebnis der VORHERIGEN Woche gibt
  // (relevant wenn neuer Zyklus gestartet aber Result noch nicht angeschaut wurde)
  // ⚠️ Läuft ab Montag 06:00 – Spoiler-Schutz nur für aktuelle Woche (erster Check)
  // Bei jedem Zykluswechsel (deadline ändert sich) erneut prüfen
  const archiveCheckDone = useRef<string | null>(null);
  useEffect(() => {
    if (!token || !myId) return;
    const currentDeadline = week?.cycle?.workout_deadline_utc ?? '';
    if (archiveCheckDone.current === currentDeadline) return;
    archiveCheckDone.current = currentDeadline;
    api.get('/duel/archive/weeks', token).then((weeks) => {
      if (!weeks?.weeks || weeks.weeks.length === 0) return;
      const latestWeek = weeks.weeks[0]; // neueste abgeschlossene Woche
      if (!latestWeek?.week_label) return;
      api.get(`/duel/archive/week/${latestWeek.week_label}`, token).then((matches) => {
        if (!matches?.matchups) return;
        // Race-Condition-Guard: Wenn der aktuelle Zyklus bereits ein unseenResult gesetzt hat,
        // Archiv-Ergebnis nicht überschreiben (aktuelle Woche hat Priorität)
        setUnseenResult((prev) => {
          if (prev) return prev; // bereits gesetzt → nicht überschreiben
          // API liefert verschachtelte Objekte: player_a.id, player_a.display_name etc.
          const myMatch = matches.matchups.find((m: any) =>
            m.player_a?.id === myId || m.player_b?.id === myId
          );
          if (!myMatch) return null;
          if (myMatch.result_seen_at_utc) return null; // bereits angesehen (DB)
          const oppName = myMatch.player_a?.id === myId
            ? (myMatch.player_b?.display_name ?? 'Opponent')
            : (myMatch.player_a?.display_name ?? 'Opponent');
          return {
            matchup_id:    myMatch.matchup_id,
            week_label:    latestWeek.week_label,
            result:        'win',
            my_points:     0,
            opp_points:    0,
            opponent_name: oppName,
          };
        });
      }).catch(() => {});
    }).catch(() => {});
  }, [token, myId, week?.cycle?.workout_deadline_utc]);

  useEffect(() => {
    if (!token || !myId) return;
    api.get(`/player/${myId}/stats`, token).then((data) => {
      if (data?.wins !== undefined) setMyStats(data as PlayerStats);
      else setMyStats(false);
    }).catch(() => setMyStats(false));
  }, [token, myId]);

  // Admin Review Notifications laden
  useEffect(() => {
    if (!token) return;
    api.get('/player/me/reviews', token).then((data) => {
      if (data?.reviews?.length) setAdminReviews(data.reviews);
    });
    api.get('/player/me/untrusted', token).then((data) => {
      if (data?.untrusted) setMyUntrusted(data);
    });
  }, [token]);

  // ── Background-Sync: eigene + Gegner-Aktivitäten beim Dashboard-Load syncen.
  // Läuft einmal pro Page-Mount (Ref-Flag). Backend hat 5-Min-Throttle.
  const hasSyncedThisSession = useRef(false);
  useEffect(() => {
    if (!token) return;
    if (hasSyncedThisSession.current) return;
    if (!week) return;
    if (!navigator.onLine) return; // Skip auto-sync when offline
    hasSyncedThisSession.current = true;

    // Skip auto-sync if last sync was < 5 min ago (avoids 429 "Too many requests")
    if (lastSyncedAt) {
      const lastSyncMs = new Date(lastSyncedAt.replace(' ', 'T') + 'Z').getTime();
      if (Date.now() - lastSyncMs < 300_000) return;
    }

    const reloadWeek = () => {
      api.get('/player/me/week', token).then((data) => {
        if (data?.my_activities) setWeek(data);
        if (data?.last_synced_at) setLastSyncedAt(data.last_synced_at);
      });
    };

    // Eigener Sync (fire & forget, Fehler werden unten behandelt)
    const mySync = api.post('/player/me/sync', {}, token).catch((err) => ({ synced: false, error: String(err) }));

    // Gegner-Sync (nur wenn kein Bot)
    const oppSync = (!week.opponent?.is_bot && week.opponent?.id)
      ? api.post('/player/sync-opponent', {}, token).catch(() => ({ synced: false }))
      : Promise.resolve(null);

    // Wenn einer der beiden Daten geholt hat → Woche neu laden
    Promise.allSettled([mySync, oppSync]).then((results) => {
      const myResult = results[0] as PromiseFulfilledResult<any>;
      const oppResult = results[1] as PromiseFulfilledResult<any>;
      const mySynced = myResult?.value?.synced || false;
      const oppSynced = oppResult?.value?.synced || false;
      if (mySynced || oppSynced) {
        setTimeout(reloadWeek, 500);
      }
      // Sync-Error anzeigen (nur beim eigenen Sync, Gegner-Sync ist optional)
      const myVal = myResult?.value;
      if (myVal && myVal.synced === false && myVal.error) {
        // Freundliche Meldung bei Strava Rate Limit
        const isRateLimit = myVal.error.toLowerCase().includes('rate limit');
        const displayError = isRateLimit
          ? `${myVal.error} – This is a beta. Sync will work again tomorrow, Strava just can't handle more requests right now.`
          : myVal.error;
        setSyncError(displayError);
      }
    });
  }, [token, week]);

  useEffect(() => {
    if (!week?.opponent || !token) return;
    api.get(`/player/${week.opponent.id}/week`, token).then((data) => {
      setOpponentActivities(data.activities ?? []);
      setOpponentWorkoutCount(data.real_workout_count ?? (data.activities ?? []).length);
    });
    api.get(`/player/${week.opponent.id}/stats`, token).then((data) => {
      if (data?.wins !== undefined) {
        setOpponentStats(data as PlayerStats);
        setOppEloRating((data as PlayerStats).elo_rating);
      }
      else setOpponentStats(false);
    }).catch(() => setOpponentStats(false));
  }, [week, token]);

  useEffect(() => {
    if (week?.cycle?.id) {
      setResultViewed(localStorage.getItem(`duel_viewed_${week.cycle.id}`) === '1');
    }
  }, [week?.cycle?.id]);

  // Cooldown-Timer für manuellen Sync
  useEffect(() => {
    if (syncCooldown <= 0) return;
    const id = setInterval(() => {
      setSyncCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [syncCooldown]);

  // Auto-Refresh: Startet Intervall sofort, pollt API erst nach Deadline bis duel_evaluate fertig ist
  useEffect(() => {
    if (!token || !week?.cycle?.workout_deadline_utc || !week?.opponent) return;
    if (week.opponent.matchup_status === 'completed') return; // schon ausgewertet → Intervall stoppen

    const doPoll = () => {
      const cycleDeadlineMs = utcMs(week.cycle.workout_deadline_utc);
      if (Date.now() <= cycleDeadlineMs) return; // Runde läuft noch → kein Polling
      api.get('/player/me/week', token).then((data) => {
        if (data?.my_activities) {
          setWeek(data);
          // Wenn Duel ausgewertet → ELO-Werte aktualisieren
          if (data?.opponent?.matchup_status === 'completed') {
            api.get('/player/me', token).then((me) => {
              if (me?.elo_rating) setEloRating(Number(me.elo_rating));
            });
            if (data?.opponent?.id) {
              api.get(`/player/${data.opponent.id}/stats`, token).then((opp) => {
                if (opp?.elo_rating !== undefined) setOppEloRating(Number(opp.elo_rating));
              });
            }
          }
        }
      });
    };

    // Sofortiger Poll wenn Deadline bereits vorbei (z.B. User öffnet App nach 18:00 UTC)
    doPoll();
    const id = setInterval(doPoll, 5000); // Alle 5 Sekunden prüfen bis Cron fertig ist
    return () => clearInterval(id);
  }, [token, week?.opponent?.matchup_status]);

  // Weekly Best Card: prüfe ob es eine nicht-gesehene Karte gibt
  useEffect(() => {
    if (!token) return;
    api.get('/player/me/weekly-best', token).then((data) => {
      if (data?.weekly_best) setShowWeeklyBest(true);
    });
  }, [token]);

  // Bet notification removed

  if (loading) return <div className="loading">Loading...</div>;
  if (!week?.cycle && token) return <div className="loading">Loading...</div>;
  if (!week?.cycle) return <div className="error">No active cycle</div>;

  const opp: Opponent | null = week.opponent;

  function utcMs(s: string): number {
    return new Date(s.replace(' ', 'T') + 'Z').getTime();
  }

  // Matchmaking läuft Montag 06:10 UTC = cycle start (06:00 UTC) + 10 Minuten
  const MATCHMAKING_OFFSET_MS = 10 * 60 * 1000;
  const cycleDeadlineMs = utcMs(week.cycle.workout_deadline_utc);
  const cycleStartMs    = utcMs(week.cycle.start_at_utc);
  // Zyklus vorbei wenn Training-Deadline überschritten (Sonntag 18:00 UTC)
  const cycleOver = nowMs > cycleDeadlineMs;
  // Countdown zeigen: kein Gegner ODER Training-Phase vorbei (nach duel_evaluate)
  const showMatchmakingCountdown = !opp || cycleOver;
  // Zielzeit: neuer Zyklus → matchmaking in wenigen Minuten; Zyklus vorbei → nächste Woche
  const matchmakingMs = cycleOver
    ? cycleStartMs + 7 * 24 * 60 * 60 * 1000 + MATCHMAKING_OFFSET_MS
    : cycleStartMs + MATCHMAKING_OFFSET_MS;
  const matchRemainMs = Math.max(0, matchmakingMs - nowMs);
  const mmDays = Math.floor(matchRemainMs / 86400000);
  const mmH    = Math.floor((matchRemainMs % 86400000) / 3600000);
  const mmM    = Math.floor((matchRemainMs % 3600000) / 60000);
  const mmS    = Math.floor((matchRemainMs % 60000) / 1000);
  function pad2(n: number): string { return String(n).padStart(2, '0'); }

  const myPoints = sumPoints(week.my_activities);
  const oppPoints = sumPoints(opponentActivities);
  const myMinutes = sumMinutes(week.my_activities);
  const oppMinutes = sumMinutes(opponentActivities);
  // Gegner-Punkte erst nach Klick auf "Duell Result" anzeigen (nicht automatisch nach Deadline)
  const resultRevealed = cycleOver && resultViewed;
  // Pokal nur anzeigen wenn Spieler bereits auf "Duell Result" geklickt hat
  // ⚠️ Kein Pokal wenn unseenResult-Banner aktiv (sonst Spoiler!)
  const winner = (resultViewed && cycleOver && opp && !unseenResult)
    ? (myPoints > oppPoints ? 'mine' : oppPoints > myPoints ? 'opp' : null)
    : null;

  function handleViewDuel() {
    if (week?.cycle?.id) {
      localStorage.setItem(`duel_viewed_${week.cycle.id}`, '1');
      setResultViewed(true);
    }
    if (week?.opponent?.matchup_id && token) {
      api.post('/duel/result/seen', { matchup_id: week.opponent.matchup_id }, token);
    }
    onNavigateToDuel();
  }

  function dismissWelcome() {
    if (token) api.post('/player/me/welcome', {}, token);
    setShowWelcome(false);
  }

  function dismissUnseenResult() {
    if (unseenResult) {
      if (token) api.post('/duel/result/seen', { matchup_id: unseenResult.matchup_id }, token);
      setUnseenResult(null);
      if (week?.cycle?.id) localStorage.setItem(`duel_viewed_${week.cycle.id}`, '1');
      setResultViewed(true);
    }
  }

  function dismissUnseenBadges() {
    if (unseenBadges.length > 0 && token) {
      unseenBadges.forEach((b: any) => {
        api.post('/player/me/badges/viewed', { badge_def_id: b.id }, token);
      });
    }
    setUnseenBadges([]);
  }

  function dismissSingleBadge(badge: any) {
    if (token) {
      api.post('/player/me/badges/viewed', { badge_def_id: badge.id }, token);
    }
    setUnseenBadges(prev => prev.filter((b: any) => b.id !== badge.id));
  }

  function getBadgeCongratsText(b: any): React.ReactNode {
    const tier = b.current_tier || 1;
    const reward = b.tier_rewards?.[tier] ?? 0;
    const name = b.name || 'Unknown';
    const texts: Record<string, string> = {
      collector: `You've been putting in serious work — the <strong>Collector</strong> badge is yours! Tier ${tier} unlocked. Expect <strong>+${reward} pts/week</strong> on top. Keep stacking those sessions!`,
      streaker: `Three wins in a row? That's no coincidence — it's <strong>Streaker!</strong> Tier ${tier} secured. Enjoy <strong>+${reward} pts/week</strong> for your winning streak. The momentum is real!`,
      underdog: `Three losses in a row — but you keep showing up. That's the <strong>Underdog!</strong> spirit. Tier ${tier} unlocked with <strong>+${reward} pts/week</strong>. Your comeback is loading…`,
      allrounder: `Running, cycling, strength and more — you do it all. The <strong>All-rounder!</strong> badge is yours! Tier ${tier} with <strong>+${reward} pts/week</strong>. Every workout 30+ minutes, no shortcuts. Variety is your superpower!`,
      nerd: `You're using every tool in the box — that's the <strong>Nerd!</strong> way. Tier ${tier} with <strong>+${reward} pts/week</strong>. Items are your secret weapon!`,
      veteran: `${tier === 3 ? '100' : tier === 2 ? '50' : '25'} weeks and counting — you're a true <strong>Veteran!</strong> Tier ${tier} with <strong>+${reward} pts/week</strong>. Loyalty pays off!`,
    };

    const text = texts[b.code] || `You earned the <strong>${name}</strong> badge — Tier ${tier}! From now on you receive <strong>+${reward} pts/week</strong>. Well deserved!`;
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  }

  function replayUnseenResult() {
    if (unseenResult) {
      if (token) api.post('/duel/result/seen', { matchup_id: unseenResult.matchup_id }, token);
      setUnseenResult(null);
      if (week?.cycle?.id) localStorage.setItem(`duel_viewed_${week.cycle.id}`, '1');
      setResultViewed(true);
      onReplayMatchup(unseenResult.matchup_id);
    }
  }

  // Manueller Strava-Sync mit 5-Minuten-Throttle (Backend-seitig + lokaler Timer)
  function handleManualSync() {
    if (syncing || syncCooldown > 0 || !token) return;
    setSyncing(true);
    setSyncMessage(null);
    api.post('/player/me/sync', {}, token).then((res) => {
      setSyncing(false);
      if (res?.synced) {
        const count = res.count ?? 0;
        const warning = res.warning ?? null;
        if (warning) {
          setSyncMessage({text: `Synced ${count} workout(s). Warning: ${warning}`, type: 'warning'});
        } else {
          setSyncMessage({text: count > 0 ? `Synced ${count} new workout(s)!` : 'No new workouts found.', type: 'success'});
        }
        // Nach erfolgreichem Sync: Wochendaten neu laden
        api.get('/player/me/week', token).then((data) => {
          if (data?.my_activities) setWeek(data);
          if (data?.last_synced_at) setLastSyncedAt(data.last_synced_at);
        });
        // 5-Min Cooldown starten
        setSyncCooldown(300);
        // Sync-Nachricht nach 8 Sekunden ausblenden
        setTimeout(() => setSyncMessage(null), 8000);
      } else if (res?.retry_after) {
        setSyncCooldown(res.retry_after);
        setSyncMessage({text: `Sync cooldown – please wait ${res.retry_after}s.`, type: 'warning'});
        setTimeout(() => setSyncMessage(null), 5000);
      } else {
        // Fehler vom Backend
        const errorMsg = res?.error ?? 'Unknown error';
        const isRateLimit = errorMsg.toLowerCase().includes('rate limit');
        const displayMsg = isRateLimit
          ? `Sync failed: ${errorMsg} – This is a beta. Sync will work again tomorrow, Strava just can't handle more requests right now.`
          : `Sync failed: ${errorMsg}`;
        setSyncMessage({text: displayMsg, type: 'error'});
        setSyncCooldown(10);
        setTimeout(() => setSyncMessage(null), 8000);
      }
    }).catch((err) => {
      setSyncing(false);
      setSyncMessage({text: `Sync failed: ${err?.message ?? 'Network error'}`, type: 'error'});
      setSyncCooldown(10);
      setTimeout(() => setSyncMessage(null), 8000);
    });
  }

  function showTopTooltip(e: React.MouseEvent<Element>, which: 'mine' | 'opp') {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTopTooltip({ which, x: rect.left + rect.width / 2, y: rect.bottom + 10 });
  }
  function hideTopTooltip() { setTopTooltip(null); }
  function toggleTopTooltip(e: React.MouseEvent<Element>, which: 'mine' | 'opp') {
    e.stopPropagation();
    if (topTooltip?.which === which) { setTopTooltip(null); }
    else { showTopTooltip(e, which); }
  }

  return (
    <>
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-header-nav">
          <button onClick={() => setShowSettingsPanel(true)} className="btn-settings btn-settings--hamburger" aria-label="Settings">
            <FontAwesomeIcon icon={faBars} />
          </button>
          <button onClick={() => setShowSettingsPanel(true)} className="btn-settings btn-settings--desktop-gear" aria-label="Settings">
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
        <img src={logo} alt="NoWeakWeek" className="dashboard-logo" />
        <h1>NoWeakWeek</h1>
      </header>

      {/* ── Settings Slide Panel (mobile) ── */}
      {showSettingsPanel && (
        <div className="settings-slide-overlay" onClick={closeSettingsPanel}>
          <div className="settings-slide-panel" onClick={(e) => e.stopPropagation()}>
            <div className="settings-slide-header">
              <button onClick={closeSettingsPanel} className="settings-slide-back" aria-label="Close settings">
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <span className="settings-slide-title">Settings</span>
            </div>
            <div className="settings-slide-body">
              <div className="settings-section">
                <h2><FontAwesomeIcon icon={faGear} /> Account</h2>
                <p className="settings-description">
                  Signed in as <strong>{myName}</strong>
                </p>
                <button className="btn-logout settings-slide-logout-btn" onClick={() => { closeSettingsPanel(); logout(); }}>
                  Logout
                </button>
              </div>
              <div className="settings-section">
                <h2 style={{ color: '#ef4444' }}><FontAwesomeIcon icon={faExclamationTriangle} /> Delete Account</h2>
                <p className="settings-description">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <button className="settings-btn-delete" onClick={() => { closeSettingsPanel(); onNavigateToSettings(); }}>
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Reward Modal — shows if player has unclaimed reward */}
      {showItemReward && (
        <ItemRewardModal
          onClaimed={() => setShowItemReward(false)}
          onDismiss={() => setShowItemReward(false)}
        />
      )}

      {unseenResult && (
        <div className="unseen-result-banner">
          <div className="unseen-result-info">
            <span className="unseen-result-label">Unseen Result</span>
            <span className="unseen-result-detail">
              🎬 Your duel against {unseenResult.opponent_name} is waiting for you!
            </span>
          </div>
          <div className="unseen-result-actions">
            <button className="unseen-result-btn-watch" onClick={replayUnseenResult}>▶ Watch</button>
            <button className="unseen-result-btn-dismiss" onClick={dismissUnseenResult}>×</button>
          </div>
        </div>
      )}

      {unseenBadges.map((b: any) => (
        <div key={b.code} className="unseen-result-banner" style={{borderColor: 'rgba(230,184,0,0.4)', background: 'rgba(230,184,0,0.06)'}}>
          <div className="unseen-result-info" style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16}}>
            <div style={{position: 'relative', width: 80, height: 80, flexShrink: 0}}>
              <div style={{width: 80, height: 80, borderRadius: '50%', border: '2px solid #e6b800', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(230,184,0,0.15)', color: '#e6b800', fontSize: '2rem'}}>
                <FontAwesomeIcon icon={getSportIcon(b.icon || 'BadgeReward')} />
              </div>
              <div className="item-tier-icons" style={{color: '#81c784', position: 'absolute', bottom: 2, left: 0, right: 0, textAlign: 'center'}}>
                {'▲'.repeat(b.current_tier || 1)}
              </div>
            </div>
            <div style={{flex: 1, minWidth: 0}}>
              <div style={{fontWeight: 800, fontSize: '1rem', color: '#e6b800', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4}}>
                New Badge!
              </div>
              <div style={{fontSize: '0.78rem', color: '#e6b800', lineHeight: 1.4}}>
                {getBadgeCongratsText(b)}
              </div>
            </div>
          </div>
          <div className="unseen-result-actions">
            <button className="unseen-result-btn-dismiss" style={{color: '#e6b800'}} onClick={() => dismissSingleBadge(b)}>x</button>
          </div>
        </div>
      ))}

      {showWelcome && myId === 1 && (
        <div className="welcome-banner">
          <button className="welcome-banner-close" onClick={dismissWelcome}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <div className="welcome-banner-header">
            <div className="welcome-banner-icon">🔥</div>
            <h2 className="welcome-banner-title">Welcome to No Weak Week!</h2>
          </div>
          <p className="welcome-banner-subtitle">
            Each week you compete against an opponent — whoever earns more training points wins and levels up.
          </p>
          <p className="welcome-banner-subtitle">
            Your points are based on the <strong>heart rate zones</strong> of your Strava activities. The longer and more intense the workout, the more points.
          </p>
          <div className="welcome-banner-bot-hint">
            <FontAwesomeIcon icon={faRobot} />
            <span>This week you're playing against a bot — next week you'll face real opponents!</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button className="welcome-banner-btn" onClick={dismissWelcome}>Let's go 🚀</button>
          </div>
        </div>
      )}

      {showOpponentProfile && opp && (
        <OpponentProfileCard
          opponentId={opp.id}
          opponentName={opp.display_name}
          onDismiss={() => {
            if (token) api.post('/duel/steckbrief/seen', { matchup_id: opp.matchup_id }, token);
            setShowOpponentProfile(false);
          }}
        />
      )}

      {showWeeklyBest && (
        <WeeklyBestCard
          onDismiss={() => setShowWeeklyBest(false)}
        />
      )}

      {adminReviews.length > 0 && (
        <div className="admin-review-banner">
          <div className="admin-review-info">
            <span className="admin-review-label">
              {adminReviews[0].decision === 'approved' ? '✅' : '🔄'} Activity Reviewed
            </span>
            <span className="admin-review-detail">
              Your workout "<strong>{adminReviews[0].activity_name}</strong>" ({adminReviews[0].sport_type}) was reviewed by an admin
              {adminReviews[0].decision === 'recalculated'
                ? <> and recalculated: <strong>{adminReviews[0].new_total_points} pts</strong> (was {adminReviews[0].old_total_points} pts) – heart rate data was unreliable</>
                : <> and approved as correct – <strong>{adminReviews[0].old_total_points} pts</strong></>
              }
            </span>
            {adminReviews.length > 1 && (
              <span className="admin-review-extra">+{adminReviews.length - 1} more review{adminReviews.length > 2 ? 's' : ''}</span>
            )}
          </div>
          <button className="admin-review-btn-dismiss" onClick={() => {
            if (token) api.post('/player/me/reviews/seen', {}, token);
            setAdminReviews([]);
          }}>×</button>
        </div>
      )}

      {myUntrusted?.untrusted && (
        <div className="untrusted-player-banner">
          <div className="untrusted-player-info">
            <span className="untrusted-player-label">⚠️ Account Under Review</span>
            <span className="untrusted-player-detail">
              Your heart rate data has been flagged as unreliable. Your workouts are currently being scored at a reduced rate ({0.20} pts/min) until an admin reviews and restores your account.
            </span>
          </div>
        </div>
      )}

      {syncError && (
        <div className="sync-error-banner">
          <div className="sync-error-info">
            <span className="sync-error-label">⚠️ Sync failed</span>
            <span className="sync-error-detail">{syncError}</span>
          </div>
          <button className="sync-error-btn-dismiss" onClick={() => setSyncError(null)}>×</button>
        </div>
      )}


      <div className="duel-card">
        <div className="versus-bar">
          <div className="versus-player">
            <div className="versus-avatar-wrap">
              {myAvatar && (
                <img
                  src={myAvatar} alt={myName} className="versus-avatar"
                  onMouseEnter={(e) => showTopTooltip(e, 'mine')}
                  onMouseLeave={hideTopTooltip}
                  onClick={() => onNavigateToProfile()}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {winner === 'mine' && <span className="versus-trophy"><FontAwesomeIcon icon={faTrophy} /></span>}
            </div>
            <span className="versus-name">{myName}</span>
            <BadgeBar badges={myBadges} side="right" />
          </div>
          <div className="vs">VS</div>
          <div className="versus-player versus-player--right">
            <div className="versus-avatar-wrap">
              {opp?.avatar_url && !opp.is_bot && (
                <img
                  src={opp.avatar_url} alt={opp.display_name} className="versus-avatar"
                  onMouseEnter={(e) => showTopTooltip(e, 'opp')}
                  onMouseLeave={hideTopTooltip}
                  onClick={() => onNavigateToPlayerProfile(opp.id)}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {!!opp?.is_bot && (
                <span
                  onMouseEnter={(e) => showTopTooltip(e, 'opp')}
                  onMouseLeave={hideTopTooltip}
                  onClick={(e) => toggleTopTooltip(e, 'opp')}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faRobot} className="versus-bot-icon" />
                </span>
              )}
              {winner === 'opp' && <span className="versus-trophy"><FontAwesomeIcon icon={faTrophy} /></span>}
            </div>
            <div className="versus-name-wrap">
              <span className="versus-name">
                {opp ? opp.display_name : 'No opponent'}
              </span>
              {opp && !opp.is_bot && opp.matchup_id && (
                <button
                  className="versus-profile-btn"
                  onClick={() => setShowOpponentProfile(true)}
                  title="View opponent profile"
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
              )}
            </div>
            <BadgeBar badges={oppBadges} side="left" />
          </div>
        </div>

        <PointsSummary
          myPoints={myPoints}
          myWorkouts={week.real_workout_count ?? week.my_activities.length}
          myMinutes={myMinutes}
          myElo={eloRating}
          opponentWorkouts={opponentWorkoutCount ?? opponentActivities.length}
          opponentPoints={oppPoints}
          opponentMinutes={oppMinutes}
          opponentElo={oppEloRating}
          revealed={resultRevealed}
        />

        {showMatchmakingCountdown && (
          <div className="matchmaking-countdown">
            <span className="matchmaking-countdown-label">Next round starts in:</span>
            <span className="matchmaking-countdown-time">
              {matchRemainMs > 0
                ? `${mmDays > 0 ? mmDays + 'd ' : ''}${pad2(mmH)}:${pad2(mmM)}:${pad2(mmS)}`
                : 'Almost time!'}
            </span>
          </div>
        )}

        <DuelCountdownButton
          cycleStart={week.cycle.start_at_utc}
          deadline={week.cycle.workout_deadline_utc}
          onViewDuel={handleViewDuel}
          testMode={myId === 1}
          matchupStatus={opp?.matchup_status}
        />
      </div>

      {/* Items Bar */}
      <ItemBar
        items={week.my_items ?? []}
        itemUsedToday={week.item_used_today ?? false}
        uiStates={uiStates}
        disabled={week?.cycle?.status !== 'open'}
        onItemUsed={() => {
          if (!token) return;
          api.get('/player/me/week', token).then((data) => {
            if (data?.my_activities) setWeek(data);
          });
          // uiStates neu laden (fuer Reveal/Trophy/Spin Daten)
          api.get('/player/me/ui-states', token).then((data: any) => {
            if (data?.states) setUiStates(data.states);
          }).catch(() => {});
          if (week?.opponent?.id) {
            api.get(`/player/${week.opponent.id}/week`, token).then((data) => {
              setOpponentActivities(data.activities ?? []);
              setOpponentWorkoutCount(data.real_workout_count ?? (data.activities ?? []).length);
            });
          }
        }}
      />

      <WeeklyCalendar
        cycleStart={week.cycle.start_at_utc}
        workoutDeadline={week.cycle.workout_deadline_utc}
        myActivities={week.my_activities}
        opponentActivities={opponentActivities}
        weekChallenges={weekChallenges}
        opponent={opp}
        myName={myName}
        myAvatar={myAvatar}
        myId={myId ?? undefined}
        onNavigateToPlayerProfile={onNavigateToPlayerProfile}
        myStats={myStats}
        opponentStats={opponentStats}
        myConsolationPoints={week.my_consolation ?? 0}
        opponentConsolationPoints={week.opponent?.opponent_consolation ?? 0}
        hideOpponentPoints={!(resultRevealed && resultViewed)}
        hideOpponentTooltips={true}
        maxSpinDays={(() => {
          // Versuche zuerst aus player_ui_state (neuer Flow)
          const spinUnlock = uiStates?.spin_unlock;
          if (spinUnlock?.days_ahead) return spinUnlock.days_ahead;
          // Fallback: aus Item (alter Flow)
          const spinItem = (week.my_items ?? []).find((i: any) => i.effect_type === 'unlock_spin' && i.is_used);
          return spinItem ? (spinItem.effect_value || 1) : 1;
        })()}
        spinStartDate={(() => {
          const spinUnlock = uiStates?.spin_unlock;
          if (spinUnlock?.unlocked_at) return spinUnlock.unlocked_at.split('T')[0];
          const spinItem = (week.my_items ?? []).find((i: any) => i.effect_type === 'unlock_spin' && i.is_used);
          return spinItem?.used_on_day ?? null;
        })()}
        spinItemId={(() => {
          const spinItem = (week.my_items ?? []).find((i: any) => i.effect_type === 'unlock_spin' && !i.is_used);
          return spinItem?.user_item_id ?? null;
        })()}
      />

      <SportBreakdownBar
        activities={week.my_activities}
        currentDayIndex={currentDayIndex}
        visible={barVisible}
        title="Points by Sport (this week)"
      />

      <LadderView levelValue={levelValue} eloRating={eloRating} topRating={topRating} myAvatar={myAvatar} myName={myName} topPlayerAvatar={topPlayerAvatar} />

      <div className="nav-section">
        <div className="nav-buttons nav-buttons--stacked">
          <button className="btn-nav" onClick={onNavigateToLeaderboard}><FontAwesomeIcon icon={faCrown} /> Leaderboard</button>
          <button className="btn-nav" onClick={onNavigateToDuelboard}><FontAwesomeIcon icon={faHandFist} /> Duelboard</button>
          <button className="btn-nav" onClick={onNavigateToArchive}><FontAwesomeIcon icon={faTrophy} /> Archive</button>
          <button className="btn-nav" onClick={onNavigateToProfile}><FontAwesomeIcon icon={faHeartPulse} /> My Profile</button>
        </div>
      </div>

      <div className="sync-section">
        <button
          className={`btn-sync${!isOnline ? ' btn-sync--offline' : ''}`}
          onClick={handleManualSync}
          disabled={syncing || syncCooldown > 0 || !isOnline}
          onMouseEnter={() => setShowSyncTooltip(true)}
          onMouseLeave={() => setShowSyncTooltip(false)}
        >
          <FontAwesomeIcon icon={faSync} className={syncing ? 'fa-spin' : ''} />
          {!isOnline
            ? 'Offline — no internet connection'
            : syncing ? 'Syncing…' : syncCooldown > 0
              ? `Sync available in ${Math.floor(syncCooldown / 60)}:${String(syncCooldown % 60).padStart(2, '0')}`
              : 'Missing workouts? Sync here!'
          }
          {showSyncTooltip && (
            <span className="sync-tooltip">
              {lastSyncedAt
                ? `Last sync: ${new Date(lastSyncedAt.replace(' ', 'T') + 'Z').toLocaleString('de-DE', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit'
                  })}`
                : 'No sync yet — activities are synced automatically daily'
              }
            </span>
          )}
        </button>
        {syncMessage && (
          <div className={`sync-message sync-message--${syncMessage.type}`}>
            {syncMessage.text}
          </div>
        )}
      </div>

      {topTooltip && (
        <div style={{ position: 'fixed', top: topTooltip.y, left: topTooltip.x, transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'none' }}>
          {topTooltip.which === 'mine'
            ? <PlayerStatsTooltip stats={myStats} name={myName} isMine={true} />
            : <PlayerStatsTooltip stats={opponentStats} name={opp?.display_name ?? 'Opponent'} isMine={false} />
          }
        </div>
      )}
    </div>
    <Footer cycleStart={week.cycle.start_at_utc} cycleEnd={week.cycle.workout_deadline_utc} />



  </>
  );
}
